class SearchModal extends HTMLElement {
  // Breathing room below the panel, and the floor it never drops under so a
  // short viewport still shows a row of categories rather than a sliver.
  static PANEL_GAP = 8;
  static PANEL_MIN_HEIGHT = 160;

  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.querySelector('button[type="reset"]').addEventListener(
      'click',
      this.reset.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    setScrollbarWidth();
    setHeaderBottomPosition();
    
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      
    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    const container = this.detailsContainer.querySelector('[tabindex="-1"]');
    const inputElement = this.detailsContainer.querySelector('input:not([type="hidden"])');

    // Focus the input on every breakpoint, so one tap on the search bar is
    // enough to start typing. trapFocus() focuses what it is handed and selects
    // an existing search term, so no separate focus() call is needed.
    trapFocus(container, inputElement || container);

    this.watchViewport();
  }

  close() {
    removeTrapFocus();
    this.unwatchViewport();
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));
  }

  // Gives the categories panel the height that is actually left below the
  // search field. It cannot be expressed in CSS: iOS keeps the layout viewport
  // at full height while the on-screen keyboard is up, so 100vh and 100dvh both
  // still measure the whole screen even though the keyboard covers half of it.
  // visualViewport is the one thing that reports the visible area.
  fitRecommendations() {
    const panel = this.querySelector('.search__categories');
    if (!panel) return;

    const viewport = window.visualViewport;
    const visibleHeight = viewport ? viewport.height : window.innerHeight;
    // Non-zero when the browser has shifted the visual viewport inside the
    // layout viewport; the panel's rect is measured against the latter.
    const shift = viewport ? viewport.offsetTop : 0;
    const top = panel.getBoundingClientRect().top - shift;
    const available = Math.round(visibleHeight - top - SearchModal.PANEL_GAP);

    panel.style.setProperty(
      '--search-reco-height',
      `${Math.max(SearchModal.PANEL_MIN_HEIGHT, available)}px`
    );
  }

  watchViewport() {
    this.fitRecommendationsEvent = this.fitRecommendationsEvent || this.fitRecommendations.bind(this);

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', this.fitRecommendationsEvent);
      viewport.addEventListener('scroll', this.fitRecommendationsEvent);
    }
    window.addEventListener('resize', this.fitRecommendationsEvent);

    // After layout, so the panel already sits under the search field.
    requestAnimationFrame(this.fitRecommendationsEvent);
  }

  unwatchViewport() {
    if (!this.fitRecommendationsEvent) return;

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.removeEventListener('resize', this.fitRecommendationsEvent);
      viewport.removeEventListener('scroll', this.fitRecommendationsEvent);
    }
    window.removeEventListener('resize', this.fitRecommendationsEvent);

    const panel = this.querySelector('.search__categories');
    if (panel) panel.style.removeProperty('--search-reco-height');
  }

  reset(event) {
    event.preventDefault();
    this.querySelector('input[type="search"]').value = '';
  }
}

customElements.define('search-modal', SearchModal);